import React from "react";
import Icon from "../Icon";
import { Card, Pill } from "../primitives";
import { normalizeLink, canViewLink } from "../../../../utils/clubLinks";
import { displayUrl, isHttpUrl, safeHttpUrl } from "./clubUtils";

// Known hosts get their platform name when a link has no stored name (otherwise every row read "Website").
const PLATFORM_HOSTS = [
  ["instagram.com", "Instagram"], ["linkedin.com", "LinkedIn"], ["tiktok.com", "TikTok"], ["facebook.com", "Facebook"],
  ["x.com", "X"], ["twitter.com", "X"], ["youtube.com", "YouTube"], ["youtu.be", "YouTube"], ["discord.gg", "Discord"],
  ["discord.com", "Discord"], ["groupme.com", "GroupMe"], ["linktr.ee", "Linktree"], ["github.com", "GitHub"],
  ["forms.gle", "Google Form"], ["docs.google.com", "Google Docs"], ["slack.com", "Slack"], ["campuslabs.com", "Engage"],
];

function linkLabel(l) {
  if (l.name) return l.name;
  const host = displayUrl(l.url).split(/[/?#]/)[0].toLowerCase();
  const hit = PLATFORM_HOSTS.find(([h]) => host === h || host.endsWith(`.${h}`));
  return hit ? hit[1] : "Website";
}

/** About tab: description + Platforms + Documents (ClubAboutTab.js). */
export default function ClubAbout({ club, isMember, isAdmin }) {
  const links = (Array.isArray(club.links) ? club.links : [])
    .map((raw) => ({ raw, ...normalizeLink(raw) }))
    .filter((l) => l.url.trim() && canViewLink(l.raw, { isMember, isAdmin }));
  const docs = (Array.isArray(club.pdfs) ? club.pdfs : [])
    .map((p, i) => ({
      name: typeof p === "string" ? `Document ${i + 1}` : p?.name || `Document ${i + 1}`,
      url: typeof p === "string" ? p : p?.url || p?.uri,
    }))
    .filter((d) => isHttpUrl(d.url));

  return (
    <div className="grid g2" style={{ alignItems: "start" }}>
      <Card pad className="col">
        <span className="label">About</span>
        <p style={{ fontSize: 15, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {club.description || "No description yet."}
        </p>
      </Card>
      <div className="col" style={{ gap: 16 }}>
        <Card pad className="col">
          <span className="label">Platforms</span>
          {links.length === 0 && <p className="meta">No platform links added yet.</p>}
          {links.map((l, i) => {
            const href = safeHttpUrl(l.url);
            const body = (
              <>
                <span className="icirc" style={{ width: 36, height: 36 }}><Icon name="link" size={16} /></span>
                <div className="grow" style={{ minWidth: 0 }}>
                  <b>{linkLabel(l)}</b>
                  <div className="meta ellip">{displayUrl(l.url)}</div>
                </div>
                {l.is_members_only ? <Pill tone="purple" icon="lock">Members only</Pill> : <Icon name="ext" size={16} />}
              </>
            );
            return href ? (
              <a key={i} className="row" style={{ color: "inherit", textDecoration: "none" }} href={href} target="_blank" rel="noopener noreferrer" data-testid={`clubDetails.link.${i}`}>{body}</a>
            ) : (
              <div key={i} className="row" style={{ opacity: 0.6 }}>{body}</div>
            );
          })}
        </Card>
        {docs.length > 0 && (
          <Card pad className="col">
            <span className="label">Documents</span>
            {docs.map((d, i) => (
              <a key={i} className="row" style={{ color: "inherit", textDecoration: "none" }} href={d.url.trim()} target="_blank" rel="noopener noreferrer" data-testid={`clubDetails.document.${i}`}>
                <span className="icirc" style={{ width: 36, height: 36, background: "var(--redSoft)", color: "var(--red)" }}><Icon name="file" size={16} /></span>
                <b className="grow ellip" style={{ textAlign: "left" }}>{d.name}</b>
                <Icon name="ext" size={16} />
              </a>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
